import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { _axios } from '../../api/service';
import { CategoryWithRoles, CategoryRole, InternalPositionsCategoryState } from '../../types/budget';

const initialState: InternalPositionsCategoryState = {
  categories: [],
  loading: false,
  error: null,
  pendingChanges: {
    added: [],
    updated: [],
    deleted: []
  }
};

export const fetchCategoriesWithRoles = createAsyncThunk(
  'internalPositionsCategory/fetchCategories',
  async () => {
    const response = await _axios.get('/api/budget-module/settings/categories/');
    const data = response.data.results || response.data;
    
    
    // Keep snake_case naming from API
    const mappedData = data.map((category: any) => ({
      ...category,
      department_roles: (category.department_roles || category.departmentRoles || []).map((role: any) => ({
        id: role.id,
        key: role.key,
        category: role.category,
        category_name: role.category_name,
        department: role.department,
        department_name: role.department_name,
        position_name: role.position_name,  // Use position_name from API
        position_shortcut: role.position_shortcut,
        default_hours: role.default_hours,
        unit: role.unit,
        unit_name: role.unit_name,
        position_order: role.position_order,
        hour_data: role.hour_data,  // Make sure hour_data is included
        total_hours: role.total_hours,  // Include total_hours field
        description: role.description  // Include description field from settings
      }))
    }));
    
    
    return mappedData;
  }
);

export const saveCategoryRoleChanges = createAsyncThunk(
  'internalPositionsCategory/saveChanges',
  async (changes: InternalPositionsCategoryState['pendingChanges']) => {
    // Handle adds
    for (const role of changes.added) {
      await _axios.post('/api/budget-module/settings/category-department-roles/', role);
    }
    
    // Handle updates
    for (const role of changes.updated) {
      await _axios.put(`/api/budget-module/settings/category-department-roles/${role.id}/`, role);
    }
    
    // Handle deletes
    for (const id of changes.deleted) {
      await _axios.delete(`/api/budget-module/settings/category-department-roles/${id}/`);
    }
    
    // Refresh data
    const response = await _axios.get('/api/budget-module/settings/categories/');
    const data = response.data.results || response.data;
    
    // Keep snake_case naming from API
    return data.map((category: any) => ({
      ...category,
      department_roles: (category.department_roles || category.departmentRoles || []).map((role: any) => ({
        id: role.id,
        key: role.key,
        category: role.category,
        category_name: role.category_name,
        department: role.department,
        department_name: role.department_name,
        position_name: role.position_name,  // Use position_name from API
        position_shortcut: role.position_shortcut,
        default_hours: role.default_hours,
        unit: role.unit,
        unit_name: role.unit_name,
        position_order: role.position_order,
        hour_data: role.hour_data,  // Make sure hour_data is included
        total_hours: role.total_hours,  // Include total_hours field
        description: role.description  // Include description field from settings
      }))
    }));
  }
);

const internalPositionsCategorySlice = createSlice({
  name: 'internalPositionsCategory',
  initialState,
  reducers: {
    addPendingRole: (state, action: PayloadAction<CategoryRole>) => {
      state.pendingChanges.added.push(action.payload);
    },
    updatePendingRole: (state, action: PayloadAction<CategoryRole>) => {
      const index = state.pendingChanges.updated.findIndex(r => r.id === action.payload.id);
      if (index >= 0) {
        state.pendingChanges.updated[index] = action.payload;
      } else {
        state.pendingChanges.updated.push(action.payload);
      }
    },
    deletePendingRole: (state, action: PayloadAction<number>) => {
      state.pendingChanges.deleted.push(action.payload);
    },
    clearPendingChanges: (state) => {
      state.pendingChanges = {
        added: [],
        updated: [],
        deleted: []
      };
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCategoriesWithRoles.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCategoriesWithRoles.fulfilled, (state, action) => {
        state.loading = false;
        state.categories = action.payload;
      })
      .addCase(fetchCategoriesWithRoles.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch categories';
      })
      .addCase(saveCategoryRoleChanges.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(saveCategoryRoleChanges.fulfilled, (state, action) => {
        state.loading = false;
        state.categories = action.payload;
        state.pendingChanges = {
          added: [],
          updated: [],
          deleted: []
        };
      })
      .addCase(saveCategoryRoleChanges.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to save changes';
      });
  }
});

export const { 
  addPendingRole, 
  updatePendingRole, 
  deletePendingRole, 
  clearPendingChanges 
} = internalPositionsCategorySlice.actions;

export default internalPositionsCategorySlice.reducer;